import type { AriaAttributes, ReactNode, RefObject } from 'react';

import { flexRender, type Row, type Table as TableType } from '@tanstack/react-table';

import { useInfiniteVirtualScroll } from '../../hooks/use-infinite-virtual-scroll';
import { Table, TableHead, TableHeader, TableRow } from '../../primitives/table';
import { VirtualTableBody, type VirtualTableBodyRenderRowArgs } from './virtual-table-body';

declare module '@tanstack/react-table' {
    interface ColumnMeta<TData, TValue> {
        /** Applied to both the header cell and every body cell of the column. */
        className?: string;
        /** Overrides `className` for the header cell only — a pinned column's header sticks on
         *  two axes where its body cells stick on one. Falls back to `className` when unset. */
        headerClassName?: string;
        /** Set directly on the header `<th>`; most columns leave this undefined. */
        ariaSort?: AriaAttributes['aria-sort'];
    }
}

type InfiniteVirtualTableProps<TData> = {
    table: TableType<TData>;
    // Passed in because React Compiler caches `table.getRowModel()` against
    // the stable `table` reference and never sees the updated rows.
    rows: Row<TData>[];
    scrollContainerRef: RefObject<HTMLDivElement | null>;
    hasNextPage: boolean;
    isFetchingNextPage: boolean;
    fetchNextPage: () => void;
    status: 'pending' | 'error' | 'success';
    error: Error | null;
    skeleton: ReactNode;
    loadingRow: ReactNode;
    emptyMessage?: string;
    /** Additive, passed through to `EmptyTableState`'s own `className`. Omitted by default. */
    emptyStateClassName?: string;
    estimateSize?: number | ((index: number) => number);
    overscan?: number;
    className?: string;
    tableClassName?: string;
    showHeader?: boolean;
    /** Rendered inside `<TableHeader>` after the header groups — never virtualized or scrolled
     *  away, for a summary/totals row pinned beneath the column headers. Omitted by default. */
    subHeaderRow?: ReactNode;
    renderRow?: (args: VirtualTableBodyRenderRowArgs<TData>) => ReactNode;
};

export function InfiniteVirtualTable<TData>({
    table,
    rows,
    scrollContainerRef,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    status,
    error,
    loadingRow,
    skeleton,
    emptyMessage,
    emptyStateClassName,
    estimateSize = 36,
    overscan = 5,
    className,
    tableClassName,
    showHeader = true,
    subHeaderRow,
    renderRow,
}: InfiniteVirtualTableProps<TData>) {
    const columns = table.getAllColumns();
    const { virtualItems, totalSize, isLoadingMore } = useInfiniteVirtualScroll({
        scrollElementRef: scrollContainerRef,
        data: rows,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
        estimateSize,
        overscan,
        status,
        error,
    });

    return (
        <div ref={scrollContainerRef} className={className}>
            <Table className={tableClassName}>
                {(showHeader || subHeaderRow) && (
                    <TableHeader>
                        {showHeader &&
                            table.getHeaderGroups().map((headerGroup) => (
                                <TableRow key={headerGroup.id} className="table-row">
                                    {headerGroup.headers.map((header) => (
                                        <TableHead
                                            key={header.id}
                                            className={
                                                header.column.columnDef.meta?.headerClassName ??
                                                header.column.columnDef.meta?.className
                                            }
                                            aria-sort={header.column.columnDef.meta?.ariaSort}
                                        >
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                        </TableHead>
                                    ))}
                                </TableRow>
                            ))}
                        {subHeaderRow}
                    </TableHeader>
                )}
                <VirtualTableBody
                    status={status}
                    rows={rows}
                    virtualItems={virtualItems}
                    totalSize={totalSize}
                    colSpan={columns.length}
                    isLoadingMore={isLoadingMore}
                    isEmpty={rows.length === 0}
                    loadingRow={loadingRow}
                    skeleton={skeleton}
                    emptyMessage={emptyMessage}
                    emptyStateClassName={emptyStateClassName}
                    renderRow={renderRow}
                />
            </Table>
        </div>
    );
}
